import React, { useState } from 'react';
import { bool, node } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import {
  FormattedMessage,
  injectIntl,
  intlShape,
} from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import {
  Form,
  FieldTextInput,
  Button,
  FieldSelect,
  CountrySelector,
} from '../../../components';

import css from './SignupForm.module.css';
import { phoneNumberValidator } from '../../../util/phoneValidation';

const SignupFormComponent = props => {
  const [countryCode, setCountryCode] = useState({ code: props.country || 'US' });
  const [lastCountryFieldValue, setLastCountryFieldValue] = useState(props.country || 'US');
  // Get language from localStorage or default to 'en'
  const lang =
    typeof window !== 'undefined' && window.localStorage
      ? window.localStorage.getItem('appLanguage') || 'en'
      : 'en';
  // Use Intl.DisplayNames if available for localized country names
  const countryDisplayNames =
    typeof Intl !== 'undefined' && Intl.DisplayNames
      ? new Intl.DisplayNames([lang], { type: 'region' })
      : null;

  return (
    <FinalForm
      {...props}
      keepDirtyOnReinitialize
      mutators={{ ...arrayMutators }}
      render={fieldRenderProps => {
        const {
          rootClassName,
          className,
          formId,
          handleSubmit,
          inProgress,
          invalid,
          intl,
          termsAndConditions,
          values,
          signupCountries,
          form: formApi,
          showAlmostThereMessage,
        } = fieldRenderProps;

        // Simplified form - no location or country fields needed

        // email
        const emailRequired = validators.required(
          intl.formatMessage({
            id: 'SignupForm.emailRequired',
          })
        );
        const emailValid = validators.emailFormatValid(
          intl.formatMessage({
            id: 'SignupForm.emailInvalid',
          })
        );

        // password
        const passwordRequiredMessage = intl.formatMessage({
          id: 'SignupForm.passwordRequired',
        });
        const passwordMinLengthMessage = intl.formatMessage(
          {
            id: 'SignupForm.passwordTooShort',
          },
          {
            minLength: validators.PASSWORD_MIN_LENGTH,
          }
        );
        const passwordMaxLengthMessage = intl.formatMessage(
          {
            id: 'SignupForm.passwordTooLong',
          },
          {
            maxLength: validators.PASSWORD_MAX_LENGTH,
          }
        );
        const passwordMinLength = validators.minLength(
          passwordMinLengthMessage,
          validators.PASSWORD_MIN_LENGTH
        );
        const passwordMaxLength = validators.maxLength(
          passwordMaxLengthMessage,
          validators.PASSWORD_MAX_LENGTH
        );
        const passwordRequired = validators.requiredStringNoTrim(
          passwordRequiredMessage
        );
        const passwordValidators = validators.composeValidators(
          passwordRequired,
          passwordMinLength,
          passwordMaxLength
        );

        const classes = classNames(rootClassName || css.root, className);
        const submitInProgress = inProgress;
        const submitDisabled = invalid || submitInProgress;



        const validatePhoneNumber = phoneNumberValidator(
          intl.formatMessage({ id: 'SignupForm.phoneNumberInvalid' }),
          countryCode.code
        );
        const phoneNumberRequired = validators.required(
          intl.formatMessage({ id: 'SignupForm.phoneNumberRequired' })
        );
        const phoneNumberValid = validators.composeValidators(
          phoneNumberRequired,
          validatePhoneNumber
        );

        // const almostThere = showAlmostThereMessage ? (
        //   <>
        //     <p className={css.heading}>
        //       <FormattedMessage
        //         id="SignupForm.almostThereHeading"
        //         defaultMessage="Almost There!"
        //       />
        //     </p>
        //     <p className={css.subheading}>
        //       <FormattedMessage
        //         id="SignupForm.almostThereSubheading"
        //         defaultMessage="Create an account or log in, and we will take you back to the page you came from."
        //       />
        //     </p>
        //   </>
        // ) : null;

        const formSpyChangeHandler = ({ values, dirty }) => {
          // Only update countryCode if the country FIELD itself changed
          // Not just because countryCode and country field are different
          if (values && values.country && values.country !== lastCountryFieldValue && dirty) {
            setLastCountryFieldValue(values.country);
            setCountryCode({ code: values.country });
            formApi.change('phoneNumber', null);
          }
        };

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            <FormSpy onChange={formSpyChangeHandler} subscription={{ values: true, dirty: true }} />
            <div>
              {/* {almostThere} */}

              {/* Name Fields - Side by side */}
              <div className={css.formSection}>
                <div className={css.nameRow}>
                  <FieldTextInput
                    className={css.nameField}
                    type="text"
                    id={formId ? `${formId}.fname` : 'fname'}
                    name="fname"
                    autoComplete="given-name"
                    label={intl.formatMessage({
                      id: 'SignupForm.firstNameLabel',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'SignupForm.firstNamePlaceholder',
                    })}
                    validate={validators.required(
                      intl.formatMessage({
                        id: 'SignupForm.firstNameRequired',
                      })
                    )}
                    iconLeft={<i className="ri-user-line"></i>}
                  />
                  <FieldTextInput
                    className={css.nameField}
                    type="text"
                    id={formId ? `${formId}.lname` : 'lname'}
                    name="lname"
                    autoComplete="family-name"
                    label={intl.formatMessage({
                      id: 'SignupForm.lastNameLabel',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'SignupForm.lastNamePlaceholder',
                    })}
                    validate={validators.required(
                      intl.formatMessage({
                        id: 'SignupForm.lastNameRequired',
                      })
                    )}
                  // iconLeft={<i className='ri-user-line'></i>}
                  />
                </div>
                <small className={css.note}>
                  <FormattedMessage
                    id="SignupForm.nameNote"
                    defaultMessage="Make sure your first and last name matches your government-issued ID."
                  />
                </small>
              </div>

              <div className={css.marginBottom}>
                <FieldSelect
                  id="country"
                  name="country"
                  className={css.selectCountry}
                  autoComplete="country"
                  label={intl.formatMessage({
                    id: 'SignupForm.countryLabel',
                  })}
                  validate={validators.required(
                    intl.formatMessage({
                      id: 'SignupForm.countryRequiredToContinue',
                    })
                  )}
                  srOnly={true}
                >
                  <option disabled className={css.countryPlaceholder} value="">
                    {intl.formatMessage({
                      id: 'SignupForm.countryPlaceholder',
                    })}
                  </option>
                  {signupCountries.map(c => (
                    <option key={c.code} value={c.code}>
                      {countryDisplayNames
                        ? countryDisplayNames.of(c.code)
                        : c.country}
                    </option>
                  ))}
                </FieldSelect>
                {/* <small className={css.note}>
                  <FormattedMessage
                    id="SignupForm.countryRequiredNote"
                    defaultMessage="* Country selection is required to continue"
                  />
                </small> */}
              </div>

              {/* Phone Field - Optional */}
              <div className={css.formSection}>
                <div className={css.phoneWrapper}>
                  <div className={css.phoneInput}>
                    <CountrySelector
                      key={countryCode.code}
                      options={signupCountries}
                      onSelect={v => {
                        setCountryCode({ code: v });
                        formApi.change('phoneNumber', null);
                      }}
                      currentUserCountry={countryCode}
                      rootClassName={css.dropdown}
                    />
                    <FieldTextInput
                      className={css.phoneNumber}
                      type="tel"
                      id={`${formId}.phoneNumber`}
                      name="phoneNumber"
                      label={intl.formatMessage({
                        id: 'SignupForm.phoneLabel',
                      })}
                      placeholder={intl.formatMessage({
                        id: 'SignupForm.phonePlaceholder',
                      })}
                      validate={phoneNumberValid}
                      iconLeft={<i className="ri-phone-line"></i>}
                    />
                  </div>
                  <small className={css.note}>
                    <FormattedMessage
                      id="SignupForm.phoneNote"
                      defaultMessage="Required for rental notifications and support."
                    />
                  </small>
                </div>
              </div>

              {/* Email */}
              <div className={css.formSection}>
                <FieldTextInput
                  type="email"
                  id={formId ? `${formId}.email` : 'email'}
                  name="email"
                  autoComplete="email"
                  label={intl.formatMessage({
                    id: 'SignupForm.emailLabel',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.emailPlaceholder',
                  })}
                  validate={validators.composeValidators(
                    emailRequired,
                    emailValid
                  )}
                  iconLeft={<i className="ri-mail-line"></i>}
                />
                <small className={css.note}>
                  <FormattedMessage
                    id="SignupForm.emailNote"
                    defaultMessage="We will email booking information and messages"
                  />
                </small>
              </div>

              {/* Password */}
              <div className={css.formSection}>
                <FieldTextInput
                  className={css.password}
                  type="password"
                  id={formId ? `${formId}.password` : 'password'}
                  name="password"
                  autoComplete="new-password"
                  label={intl.formatMessage({
                    id: 'SignupForm.passwordLabel',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.passwordPlaceholder',
                  })}
                  validate={passwordValidators}
                  iconLeft={<i className="ri-lock-2-line"></i>}
                />
                <small className={css.passwordNote}>
                  <FormattedMessage
                    id="SignupForm.passwordNote"
                    defaultMessage="Must be at least 8 characters."
                  />
                </small>
              </div>
            </div>

            <div className={css.bottomWrapper}>
              {termsAndConditions}
              <Button
                className={css.signUpBtn}
                type="submit"
                inProgress={submitInProgress}
                disabled={submitDisabled}
              >
                <FormattedMessage id="SignupForm.signUp" />
              </Button>
            </div>
          </Form>
        );
      }}
    />
  );
};

SignupFormComponent.defaultProps = { inProgress: false };

SignupFormComponent.propTypes = {
  inProgress: bool,
  termsAndConditions: node.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const SignupForm = compose(injectIntl)(SignupFormComponent);
SignupForm.displayName = 'SignupForm';

export default SignupForm;
